/**
 * The classroom, client side: API calls, the live-room connection, and a tiny
 * path router (the app has five screens; a router library would be most of
 * the bundle).
 */
import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react'
import { api as request, get, post } from './api'
import type { AnswerValue, Module, Pool, Result, Stats, Text } from '../../shared/classroom'
import type { Look, Slot } from '../../shared/shop'

/* ── Router ───────────────────────────────────────────────────────────── */

const listeners = new Set<() => void>()
window.addEventListener('popstate', () => listeners.forEach((l) => l()))

export function go(path: string): void {
  if (location.pathname === path) return
  history.pushState(null, '', path)
  listeners.forEach((l) => l())
  window.scrollTo(0, 0)
}

export function usePath(): string {
  return useSyncExternalStore(
    (cb) => { listeners.add(cb); return () => listeners.delete(cb) },
    () => location.pathname,
  )
}

/* ── API ──────────────────────────────────────────────────────────────── */

export interface Me {
  teacher: boolean
  xp: number
  wallet: number
  owned: string[]
  look: Look
  level: { index: number; title: Text; floor: number; next: number | null; progress: number }
  stats: Stats
  badges: BadgeView[]
  champion: boolean
  milestones: { key: string; title: Text; doneAt: number | null }[]
  classes: { id: string; name: string; code: string }[]
  teaching: { id: string; name: string; code: string }[]
  live: { id: string; class_id: string; lesson_id: string; started_at: number }[]
  practiced: Record<string, number>
  taught: string[]
}

let contentCache: Promise<Module[]> | null = null
/**
 * What a person can see depends on their classes and their own lessons, so
 * the cache is dropped whenever either changes (and on sign-in or sign-out).
 */
export function resetContent(): void { contentCache = null }
export function loadContent(): Promise<Module[]> {
  contentCache ??= get<{ modules: Module[] }>('/api/content').then((r) => r.modules).catch((e) => { contentCache = null; throw e })
  return contentCache
}

export function useContent(): Module[] | null {
  const [m, setM] = useState<Module[] | null>(null)
  useEffect(() => { void loadContent().then(setM) }, [])
  return m
}

export const api = {
  me: () => get<Me>('/api/me/classroom'),
  join: (code: string) => post<{ class: { id: string; name: string } }>('/api/join', { code }).finally(resetContent),
  createClass: (name: string) => post<{ class: { id: string; code: string } }>('/api/classes', { name }),
  classDetail: (id: string) => get<ClassDetail>(`/api/classes/${id}`),
  startRun: (classId: string, lessonId: string) => post<{ run: { id: string } }>(`/api/classes/${classId}/runs`, { lessonId }),
  run: (id: string) => get<RunInfo>(`/api/runs/${id}`),
  practice: (lessonId: string, stepId: string, answer: AnswerValue) => post<{ result: Result; xp: number }>('/api/practice', { lessonId, stepId, answer }),
  verify: (classId: string, userId: string, key: string, done: boolean) => post<{ ok: true }>(`/api/classes/${classId}/milestones`, { userId, key, done }),
  studentMilestones: (classId: string, userId: string) => get<{ milestones: { key: string; title: Text; doneAt: number | null }[] }>(`/api/classes/${classId}/students/${userId}/milestones`),
  grants: (classId: string, userId: string) => get<{ grants: Grant[] }>(`/api/classes/${classId}/students/${userId}/grants`),
  grant: (classId: string, userId: string, xp: number, reason: string) => post<{ grants: Grant[] }>(`/api/classes/${classId}/students/${userId}/grants`, { xp, reason }),
  buy: (item: string) => post<{ ok: true }>('/api/shop/buy', { item }),
  equip: (slot: Slot, item: string | null) => post<{ ok: true }>('/api/shop/equip', { slot, item }),
  feedback: (text: string) => post<{ ok: true }>('/api/feedback', { text, where: location.pathname }),
  resolve: (id: string) => post<{ ok: true }>(`/api/reteach/${id}/resolve`, {}),
  library: () => get<Library>('/api/lessons'),
  lesson: (id: string) => get<{ lesson: import('../../shared/classroom').Lesson; meta: MyLesson }>(`/api/lessons/${id}`),
  draftWithAi: (input: { topic: string; grade: string; minutes: number; notes: string }) =>
    post<{ lesson: MyLesson; tokens: number }>('/api/lessons/ai', input).finally(resetContent),
  draftFromFile: async (file: File, notes: string) => {
    const form = new FormData()
    form.append('file', file)
    form.append('notes', notes)
    // FormData sets its own multipart Content-Type; do not pass a JSON body.
    return request<{ lesson: MyLesson; tokens: number }>('/api/lessons/upload', { method: 'POST', body: form }).finally(resetContent)
  },
  updateLesson: (id: string, patch: { status?: 'draft' | 'ready'; title?: Text }) =>
    request<{ lesson: MyLesson }>(`/api/lessons/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }).finally(resetContent),
  deleteLesson: (id: string) => request<{ ok: true }>(`/api/lessons/${id}`, { method: 'DELETE' }).finally(resetContent),
  shareLesson: (id: string, handle: string) => post<{ ok: true; to: string }>(`/api/lessons/${id}/share`, { handle }),
  respondShare: (id: string, accept: boolean) => post<{ ok: true }>(`/api/lessons/shares/${id}`, { accept }).finally(resetContent),
  addLessons: (classId: string, lessonIds: string[]) =>
    post<{ added: string[]; lessons: ClassLesson[] }>(`/api/classes/${classId}/lessons`, { lessonIds }).finally(resetContent),
  removeLesson: (classId: string, lessonId: string) =>
    request<{ lessons: ClassLesson[] }>(`/api/classes/${classId}/lessons/${encodeURIComponent(lessonId)}`, { method: 'DELETE' }).finally(resetContent),
  exportUrl: (classId: string) => `/api/classes/${classId}/export.csv`,
  deleteClass: (classId: string, confirm: string) => request<{ ok: true }>(`/api/classes/${classId}`, { method: 'DELETE', body: JSON.stringify({ confirm }) }),
  removeStudent: (classId: string, userId: string) => request<{ ok: true; deleted: boolean }>(`/api/classes/${classId}/students/${userId}`, { method: 'DELETE', body: '{}' }),
  /** A PIN account made with core's delegate API, then put in this class. */
  addPinStudent: async (classId: string, input: { handle: string; displayName: string; pin: string }) => {
    const { id } = await post<{ id: string; handle: string }>('/api/delegates', input)
    return post<{ ok: true }>(`/api/classes/${classId}/students`, { userId: id })
  },
}

export interface BadgeView { key: string; title: Text; desc: Text; tier: number; tierName: Text; value: number; next: number | null; tiers: number[] }

export interface Reteach { id: string; user_id: string; name: string; lesson_id: string; step_ids: string[]; created_at: number }

/** XP a teacher gave by hand. `by` is the teacher's name, or null when it went in directly. */
export interface Grant { id: string; xp: number; reason: string; by: string | null; createdAt: number }

export interface ClassDetail {
  class: { id: string; name: string; code: string }
  members: { id: string; name: string; handle: string; xp: number; right: number; answered: number; idk: number }[]
  runs: { id: string; lesson_id: string; started_at: number; ended_at: number | null }[]
  reteach: Reteach[]
  lessons: ClassLesson[]
}

export interface ClassLesson { id: string; title: Text; minutes: number; questions: number; builtin: boolean }

/** One of a teacher's own lessons, as the library lists it. */
export interface MyLesson {
  id: string; title: Text; summary: Text; minutes: number; questions: number; steps: number
  source: 'ai' | 'upload' | 'shared'; sourceNote: string | null; status: 'draft' | 'ready'; createdAt: number
}
export interface Library {
  lessons: MyLesson[]
  incoming: { id: string; lessonId: string; title: Text; from: string; fromHandle: string; createdAt: number }[]
  builtin: { module: Module['module']; lessons: { id: string; title: Text; minutes: number; questions: number }[] }[]
  tokens: { balance: number; draftCost: number }
  ai: boolean
}

export interface RunInfo {
  run: { id: string; class_id: string; lesson_id: string; started_at: number; ended_at: number | null }
  board: { user_id: string; name: string; xp: number; right: number; answered: number }[]
  role: 'teacher' | 'student'
  code?: string
}

/* ── The live room ────────────────────────────────────────────────────── */

export interface LiveState {
  t: 'state'
  lessonId: string | null
  step: number
  stepId: string | null
  total: number
  open: boolean
  reveal: boolean
  callOn: { userId: string; name: string; pool: Pool; stepId: string; result: Result | null; kind: string } | null
  ended: boolean
  answered: number
  present: number
}

export interface Tally {
  t: 'tally'
  stepId: string | null
  present: { id: string; name: string }[]
  answers: Record<string, { result: Result; value: AnswerValue }>
  reteach: Reteach[]
}

export interface Graded { t: 'graded'; stepId: string; result: Result; xp: number; streak: number; repeat?: boolean }

/** A live answer is in, but not graded — it can still change until the question closes. */
export interface Received { t: 'received'; stepId: string; answer: AnswerValue }

export type Inbound =
  | LiveState | Tally | Graded | Received
  | { t: 'refused'; stepId: string; why: string }
  | { t: 'reteach'; stepIds: string[] }
  | { t: 'ended'; summary: { board: RunInfo['board'] } | null }
  | { t: 'callon-empty'; pool: Pool }
  | { t: 'bonus'; xp: number }
  | { t: 'pong' }

export type Status = 'connecting' | 'open' | 'reconnecting' | 'ended' | 'gone'

/**
 * One WebSocket to the room, reconnecting with backoff. A phone that locks
 * its screen drops the socket; coming back must just work. A 410/404 on the
 * handshake can't be seen from a WebSocket, so after repeated failures we ask
 * the REST API whether the run has ended.
 */
export function useLive(runId: string, onMessage: (m: Inbound) => void) {
  const [status, setStatus] = useState<Status>('connecting')
  const ws = useRef<WebSocket | null>(null)
  const handler = useRef(onMessage)
  handler.current = onMessage

  useEffect(() => {
    let stopped = false
    let tries = 0
    let timer: ReturnType<typeof setTimeout> | undefined
    let ping: ReturnType<typeof setInterval> | undefined

    const connect = () => {
      if (stopped) return
      const url = `${location.protocol === 'https:' ? 'wss' : 'ws'}://${location.host}/api/live/${runId}/ws`
      const sock = new WebSocket(url)
      ws.current = sock
      sock.onopen = () => {
        tries = 0
        setStatus('open')
        ping = setInterval(() => { try { sock.send('{"t":"ping"}') } catch { /* closing */ } }, 25000)
      }
      sock.onmessage = (e) => {
        try {
          const m = JSON.parse(e.data as string) as Inbound
          if (m.t === 'ended' || (m.t === 'state' && m.ended)) setStatus('ended')
          handler.current(m)
        } catch { /* ignore */ }
      }
      sock.onclose = async () => {
        clearInterval(ping)
        if (stopped) return
        tries++
        if (tries >= 3) {
          try {
            const info = await api.run(runId)
            if (info.run.ended_at) { setStatus('ended'); return }
          } catch {
            setStatus('gone')
            return
          }
        }
        setStatus((s) => (s === 'ended' ? s : 'reconnecting'))
        timer = setTimeout(connect, Math.min(8000, 500 * 2 ** tries))
      }
    }
    const onVisible = () => {
      if (document.visibilityState === 'visible' && ws.current?.readyState === WebSocket.CLOSED) {
        clearTimeout(timer)
        connect()
      }
    }
    document.addEventListener('visibilitychange', onVisible)
    connect()
    return () => {
      stopped = true
      clearTimeout(timer)
      clearInterval(ping)
      document.removeEventListener('visibilitychange', onVisible)
      ws.current?.close()
    }
  }, [runId])

  const send = useCallback((msg: Record<string, unknown>) => {
    try { ws.current?.send(JSON.stringify(msg)) } catch { /* reconnecting */ }
  }, [])

  return { status, send }
}
